// The editor's tabs on one project in this browser (spec docs/specs/editor/conflict-keeps-draft.md):
// a BroadcastChannel named for the project. Each editor tab says it is open, answers another
// tab's open, says when it closes and says each version it saves. So a second tab knows the
// project is open twice, and learns of a newer version before its own save meets the 409.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/** What one editor tab tells the others; `tab` is the sender's id, which it ignores coming back. */
export type TabMessage =
  | { kind: "open"; tab: string }
  | { kind: "here"; tab: string }
  | { kind: "closed"; tab: string }
  | { kind: "saved"; tab: string; version: number };

/** The channel's name for a project. */
export function channelName(projectId: string): string {
  return `createdai-editor-${projectId}`;
}

/** Whether a frame is one of ours: another version of the app may say something else. */
function isTabMessage(data: unknown): data is TabMessage {
  if (typeof data !== "object" || data === null || !("tab" in data) || typeof data.tab !== "string") return false;
  if (!("kind" in data)) return false;
  if (data.kind === "saved") return "version" in data && typeof data.version === "number";
  return data.kind === "open" || data.kind === "here" || data.kind === "closed";
}

export interface ProjectTabs {
  /** How many other tabs of this browser have the project open. */
  others: number;
  /** Tells the other tabs that this one saved `version`. */
  announce: (version: number) => void;
}

/**
 * The project's other editor tabs in this browser. `onSaved` hears each version another tab
 * saves. Without `BroadcastChannel` there are none, and nothing is said.
 */
export function useProjectTabs(projectId: string, onSaved: (version: number) => void): ProjectTabs {
  const [others, setOthers] = useState<ReadonlySet<string>>(() => new Set());
  // The handler of the latest render, in place before any message can arrive.
  const heard = useRef(onSaved);
  useLayoutEffect(() => {
    heard.current = onSaved;
  });
  const open = useRef<{ tab: string; channel: BroadcastChannel } | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const tab = crypto.randomUUID();
    const channel = new BroadcastChannel(channelName(projectId));
    open.current = { tab, channel };
    const post = (message: TabMessage) => channel.postMessage(message);
    const meet = (other: string, present: boolean) =>
      setOthers((current) => {
        if (current.has(other) === present) return current;
        const next = new Set(current);
        if (present) next.add(other);
        else next.delete(other);
        return next;
      });
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (!isTabMessage(message) || message.tab === tab) return;
      switch (message.kind) {
        case "open":
          meet(message.tab, true);
          post({ kind: "here", tab });
          break;
        case "here":
          meet(message.tab, true);
          break;
        case "closed":
          meet(message.tab, false);
          break;
        case "saved":
          meet(message.tab, true);
          heard.current(message.version);
          break;
      }
    };
    post({ kind: "open", tab });
    const leave = () => post({ kind: "closed", tab });
    window.addEventListener("pagehide", leave);
    return () => {
      window.removeEventListener("pagehide", leave);
      leave();
      channel.close();
      open.current = null;
      setOthers(new Set());
    };
  }, [projectId]);
  const announce = useCallback((version: number) => {
    const at = open.current;
    at?.channel.postMessage({ kind: "saved", tab: at.tab, version } satisfies TabMessage);
  }, []);
  return { others: others.size, announce };
}
